import { useFetch } from "../../lib/useFetch";

type DayRow = { day: string; calls: number; tokensIn: number; tokensOut: number; costMicros: number };
type SurfaceRow = { surface: string; calls: number; costMicros: number };
type MatterRow = { matterId: string; calls: number; costMicros: number };

type Summary = {
  days: DayRow[];
  surfaces: SurfaceRow[];
  matters: MatterRow[];
  windowDays: number;
};

function usd(micros: number): string {
  // micros are micro-USD (1e-6 USD). Format with 4 decimals so a $0.0012 charge stays visible.
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

export default function AICostsPage() {
  const { data, loading, error } = useFetch<Summary>("/api/v1/tenant/ai-calls/summary?days=30");

  const totalCost = (data?.days ?? []).reduce((acc, d) => acc + d.costMicros, 0);
  const totalCalls = (data?.days ?? []).reduce((acc, d) => acc + d.calls, 0);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">AI costs &amp; usage</h1>
      <p className="mt-2 text-sm text-slate-500">
        Per-day, per-surface, per-matter rollup of every Claude call. Window: trailing {data?.windowDays ?? 30} days.
      </p>

      {loading && <p className="mt-4 text-slate-500">…</p>}
      {error && <p className="mt-4 text-rose-600">{error}</p>}

      {data && (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-md bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs uppercase text-slate-500">Total cost</div>
              <div className="mt-1 text-2xl font-semibold text-brand-900">{usd(totalCost)}</div>
            </div>
            <div className="rounded-md bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs uppercase text-slate-500">Total calls</div>
              <div className="mt-1 text-2xl font-semibold text-brand-900">{totalCalls}</div>
            </div>
            <div className="rounded-md bg-white p-4 ring-1 ring-slate-200">
              <div className="text-xs uppercase text-slate-500">Per call (avg)</div>
              <div className="mt-1 text-2xl font-semibold text-brand-900">
                {totalCalls > 0 ? usd(totalCost / totalCalls) : "—"}
              </div>
            </div>
          </div>

          <h2 className="mt-8 text-lg font-semibold text-brand-900">By day</h2>
          <table className="mt-2 min-w-full rounded-md border border-slate-200 bg-white text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase">Day</th>
                <th className="px-4 py-2 text-right text-xs uppercase">Calls</th>
                <th className="px-4 py-2 text-right text-xs uppercase">Tokens in</th>
                <th className="px-4 py-2 text-right text-xs uppercase">Tokens out</th>
                <th className="px-4 py-2 text-right text-xs uppercase">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.days.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-slate-500" colSpan={5}>
                    No AI calls in the window.
                  </td>
                </tr>
              )}
              {data.days.map((d) => (
                <tr key={d.day} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{d.day}</td>
                  <td className="px-4 py-2 text-right">{d.calls}</td>
                  <td className="px-4 py-2 text-right">{d.tokensIn.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{d.tokensOut.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-semibold">{usd(d.costMicros)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 className="mt-8 text-lg font-semibold text-brand-900">By surface</h2>
          <ul className="mt-2 space-y-1">
            {data.surfaces.map((s) => (
              <li
                key={s.surface}
                className="flex items-center justify-between rounded bg-white px-4 py-2 ring-1 ring-slate-200"
              >
                <span className="font-mono text-xs">{s.surface}</span>
                <span className="text-xs text-slate-500">{s.calls} calls</span>
                <span className="font-semibold">{usd(s.costMicros)}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-8 text-lg font-semibold text-brand-900">Top matters by cost</h2>
          <ul className="mt-2 space-y-1">
            {data.matters.length === 0 && (
              <li className="text-slate-500">No matter-scoped calls yet.</li>
            )}
            {data.matters.map((m) => (
              <li
                key={m.matterId}
                className="flex items-center justify-between rounded bg-white px-4 py-2 ring-1 ring-slate-200"
              >
                <span className="font-mono text-xs">{m.matterId}</span>
                <span className="text-xs text-slate-500">{m.calls} calls</span>
                <span className="font-semibold">{usd(m.costMicros)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
